import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { plottingApi } from '@/api';
import { PlotConfiguration, PlottingInterfaceProps, UsePlottingInterfaceReturn } from '../types';
import { convertVariablesToDict, createInitialPlotConfig } from '../constants';

/**
 * Custom hook for PlottingInterface state management and API operations
 */
export const usePlottingInterface = ({
  sessionId,
  inputVariables = [],
  outputVariables = [],
  modelId,
  onPlotGenerated
}: PlottingInterfaceProps): UsePlottingInterfaceReturn => {
  const { t } = useTranslation('training');
  const abortControllerRef = useRef<AbortController | null>(null);
  const inputVariablesRef = useRef(inputVariables);
  const outputVariablesRef = useRef(outputVariables);

  // Keep refs in sync with props
  useEffect(() => {
    inputVariablesRef.current = inputVariables;
    outputVariablesRef.current = outputVariables;
  });

  // Initialize plotConfig with EMPTY timeComponents - will be populated from API
  const [plotConfig, setPlotConfig] = useState<PlotConfiguration>(() =>
    createInitialPlotConfig(inputVariables, outputVariables, [])
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingVariables, setIsLoadingVariables] = useState(true); // Loading until API returns
  const [error, setError] = useState<string | null>(null);
  const [generatedPlot, setGeneratedPlot] = useState<string | null>(null);
  const [showPlot, setShowPlot] = useState(false);

  // Fetch available variables from API
  const fetchVariables = useCallback(async () => {
    if (!sessionId) {
      setIsLoadingVariables(false);
      return;
    }

    if (abortControllerRef.current) {
      try {
        abortControllerRef.current.abort();
      } catch {
        // Ignore abort errors
      }
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    setIsLoadingVariables(true);

    // Track start time to ensure minimum loading display
    const startTime = Date.now();
    const MIN_LOADING_TIME = 500; // Show spinner for at least 500ms

    try {
      const result = await plottingApi.getPlotVariables(sessionId, signal) as {
        success: boolean;
        input_variables?: string[];
        output_variables?: string[];
        time_components?: string[];
      };

      if (result.success) {
        const fetchedInputVariables = result.input_variables || [];
        const fetchedOutputVariables = result.output_variables || [];
        // Use ONLY what backend returns - no hardcoded fallback
        const fetchedTimeComponents = result.time_components || [];

        setPlotConfig(prev => ({
          ...prev,
          inputVariables: fetchedInputVariables.map((name: string) => ({ name, selected: false })),
          outputVariables: fetchedOutputVariables.map((name: string) => ({ name, selected: false })),
          forecastVariables: fetchedOutputVariables.map((name: string) => ({ name, selected: false })),
          timeComponents: fetchedTimeComponents.map((name: string) => ({ name, selected: false }))
        }));
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      setError(t('training.plotting.fetchError'));
      // Fallback to prop values if API fails - no hardcoded TIME components
      setPlotConfig(prev => ({
        ...prev,
        inputVariables: inputVariablesRef.current.map(name => ({ name, selected: false })),
        outputVariables: outputVariablesRef.current.map(name => ({ name, selected: false })),
        forecastVariables: outputVariablesRef.current.map(name => ({ name, selected: false })),
        timeComponents: [] // Empty - no hardcoded fallback
      }));
    } finally {
      // Ensure loading spinner is visible for minimum time
      const elapsed = Date.now() - startTime;
      const remainingTime = MIN_LOADING_TIME - elapsed;

      if (remainingTime > 0) {
        setTimeout(() => setIsLoadingVariables(false), remainingTime);
      } else {
        setIsLoadingVariables(false);
      }
    }
  }, [sessionId, t]);

  useEffect(() => {
    fetchVariables();

    return () => {
      // Cleanup: abort ongoing fetch if component unmounts
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchVariables]);

  // Variable change handlers
  const handleInputVariableChange = useCallback((index: number, selected: boolean) => {
    setPlotConfig(prev => ({
      ...prev,
      inputVariables: prev.inputVariables.map((variable, i) =>
        i === index ? { ...variable, selected } : variable
      )
    }));
  }, []);

  const handleTimeComponentChange = useCallback((index: number, selected: boolean) => {
    setPlotConfig(prev => ({
      ...prev,
      timeComponents: prev.timeComponents.map((variable, i) =>
        i === index ? { ...variable, selected } : variable
      )
    }));
  }, []);

  const handleOutputVariableChange = useCallback((index: number, selected: boolean) => {
    setPlotConfig(prev => ({
      ...prev,
      outputVariables: prev.outputVariables.map((variable, i) =>
        i === index ? { ...variable, selected } : variable
      )
    }));
  }, []);

  const handleForecastVariableChange = useCallback((index: number, selected: boolean) => {
    setPlotConfig(prev => ({
      ...prev,
      forecastVariables: prev.forecastVariables.map((variable, i) =>
        i === index ? { ...variable, selected } : variable
      )
    }));
  }, []);

  // Plot settings handlers
  const handleNumSubplotsChange = useCallback((value: number) => {
    setPlotConfig(prev => ({
      ...prev,
      num_sbpl: Math.max(1, Math.min(50, value))
    }));
  }, []);

  const handleXAxisChange = useCallback((value: string) => {
    setPlotConfig(prev => ({ ...prev, x_sbpl: value }));
  }, []);

  const handleYAxisFormatChange = useCallback((value: string) => {
    setPlotConfig(prev => ({ ...prev, y_sbpl_fmt: value }));
  }, []);

  const handleYAxisConfigChange = useCallback((value: string) => {
    setPlotConfig(prev => ({ ...prev, y_sbpl_set: value }));
  }, []);

  // Generate plot handler
  const handleGeneratePlot = useCallback(async () => {
    if (!sessionId) {
      setError(t('training.plotting.sessionRequired'));
      return;
    }

    // Validate that at least one variable is selected
    const hasSelection =
      plotConfig.inputVariables.some(v => v.selected) ||
      plotConfig.timeComponents.some(v => v.selected) ||
      plotConfig.outputVariables.some(v => v.selected) ||
      plotConfig.forecastVariables.some(v => v.selected);

    if (!hasSelection) {
      setError(t('training.plotting.selectVariableRequired'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Combine input variables and time components for df_plot_in
      const allInputVariables = [
        ...plotConfig.inputVariables,
        ...plotConfig.timeComponents
      ];

      // Prepare plot data structure matching original code
      const plotData = {
        session_id: sessionId,
        ...(modelId && { model_id: modelId }), // Include model_id if provided

        // Main plot settings (matching original variable names)
        plot_settings: {
          num_sbpl: plotConfig.num_sbpl,
          x_sbpl: plotConfig.x_sbpl,
          y_sbpl_fmt: plotConfig.y_sbpl_fmt,
          y_sbpl_set: plotConfig.y_sbpl_set
        },

        // Convert to format expected by backend (matching df_plot_in/out/fcst structure)
        df_plot_in: convertVariablesToDict(allInputVariables),
        df_plot_out: convertVariablesToDict(plotConfig.outputVariables),
        df_plot_fcst: convertVariablesToDict(plotConfig.forecastVariables)
      };

      const result = await plottingApi.generatePlot(plotData) as {
        success: boolean;
        plot_data?: string;
        error?: string;
      };

      if (result.success) {
        setGeneratedPlot(result.plot_data || null);
        setShowPlot(true);
        if (onPlotGenerated && result.plot_data) {
          onPlotGenerated(result.plot_data);
        }
      } else {
        setError(result.error || t('training.plotting.generateError'));
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      // Show actual backend error message instead of generic "network error"
      const errorMessage = err instanceof Error ? err.message : t('training.plotting.networkError');
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, modelId, plotConfig, onPlotGenerated, t]);

  // Close plot handler
  const closePlot = useCallback(() => {
    setShowPlot(false);
    setGeneratedPlot(null);
  }, []);

  return {
    plotConfig,
    isLoading,
    isLoadingVariables,
    error,
    generatedPlot,
    showPlot,
    handleInputVariableChange,
    handleTimeComponentChange,
    handleOutputVariableChange,
    handleForecastVariableChange,
    handleNumSubplotsChange,
    handleXAxisChange,
    handleYAxisFormatChange,
    handleYAxisConfigChange,
    handleGeneratePlot,
    closePlot
  };
};
